/**
 * Formulário de uma visita, embutido na coluna ao lado da grade.
 *
 * Papel: registra quem passa pela casa e em que período, com a observação livre
 * que costuma guardar o horário, num bloco cinza que abre entre a lista de
 * diárias e o botão "+ Incluir diárias".
 *
 * Como usar: quem monta este componente passa `visita` para editar ou `null`
 * para criar, e deverá dar a ele uma `key` diferente por registro. Sem a `key`,
 * o React reaproveita a mesma instância ao trocar de visita e os campos
 * continuam com o que a pessoa via antes. O botão de gravar diz "Confirmar" na
 * criação e "Atualizar" na edição.
 *
 * O visitante é escolhido entre os prestadores cadastrados na própria seção, e
 * a criação já abre no primeiro deles. A diária em edição cujo visitante saiu
 * da lista entra com o nome dele nas opções, depois dos demais: sem isso, a
 * escolha ficaria vazia e quem edita trocaria o visitante sem perceber.
 *
 * A observação tem a altura de uma linha e cresce com o texto: é um campo de
 * várias linhas, e não um campo de texto simples, porque o campo simples
 * descartaria em silêncio a quebra de linha de uma observação já gravada, na
 * primeira vez que ela fosse salva de novo.
 */
import { type NovaVisita, type Visita, novaVisitaSchema } from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, type FieldPath, useForm } from 'react-hook-form';

import { aplicarErroDaApi, tradutorPorMapa } from '@/shared/lib/erro-de-formulario';
import { Button } from '@/shared/ui/button';
import { CampoMascarado } from '@/shared/ui/campo-mascarado';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import { useSalvarVisita } from '../hooks/usar-calendario';
import { VISITA_EM_BRANCO, type VisitaNoFormulario } from '../types';
import { CAMPO_EMBUTIDO, FORMULARIO_EMBUTIDO, ROTULO_EMBUTIDO } from './estilo-embutido';

interface FormularioVisitaProps {
  /** A visita a editar, ou `null` para criar uma nova. */
  visita: Visita | null;
  /** A casa do calendário aberto, que é quem recebe a visita. */
  residenciaId: string;
  /** Os nomes dos prestadores cadastrados, na ordem da lista. */
  prestadores: string[];
  onSalvo: () => void;
  onCancelar: () => void;
}

/** Os campos que a API pode apontar, cada um no caminho do formulário. */
const CAMPOS: Record<string, FieldPath<VisitaNoFormulario> | undefined> = {
  residenciaId: 'residenciaId',
  visitante: 'visitante',
  inicio: 'inicio',
  fim: 'fim',
  observacao: 'observacao',
};

/**
 * O formulário de uma visita.
 *
 * @param visita - O registro a editar, ou `null`.
 * @param residenciaId - A casa do calendário aberto.
 * @param prestadores - Os nomes oferecidos na escolha do visitante; a criação
 *   começa no primeiro.
 * @param onSalvo - Chamado depois de gravar, para fechar o bloco.
 * @param onCancelar - Chamado ao desistir, sem gravar nada.
 * @returns O bloco do formulário, com o visitante, o período e a observação.
 */
export function FormularioVisita({
  visita,
  residenciaId,
  prestadores,
  onSalvo,
  onCancelar,
}: FormularioVisitaProps) {
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const salvar = useSalvarVisita();

  const form = useForm<VisitaNoFormulario>({
    resolver: zodResolver(novaVisitaSchema),
    defaultValues:
      visita === null
        ? { ...VISITA_EM_BRANCO, residenciaId, visitante: prestadores[0] ?? '' }
        : {
            residenciaId,
            visitante: visita.visitante,
            inicio: visita.inicio,
            fim: visita.fim,
            observacao: visita.observacao ?? '',
          },
  });

  const enviando = salvar.isPending;
  const opcoes =
    visita !== null && !prestadores.includes(visita.visitante)
      ? [...prestadores, visita.visitante]
      : prestadores;

  const handleSalvar = form.handleSubmit(async (dados) => {
    setErroGeral(null);
    try {
      const validada: NovaVisita = novaVisitaSchema.parse(dados);
      await salvar.mutateAsync({ id: visita?.id ?? null, dados: validada });
      onSalvo();
    } catch (erro) {
      setErroGeral(aplicarErroDaApi(erro, form, tradutorPorMapa(CAMPOS)));
    }
  });

  return (
    <form
      aria-label={visita === null ? 'Nova diária' : 'Editar diária'}
      onSubmit={(evento) => void handleSalvar(evento)}
      className={FORMULARIO_EMBUTIDO}
    >
      <div>
        <Label htmlFor="visita-visitante" className={ROTULO_EMBUTIDO}>
          Visitante
        </Label>
        <Controller
          control={form.control}
          name="visitante"
          render={({ field }) => (
            <Select
              value={field.value === '' ? undefined : field.value}
              onValueChange={field.onChange}
              disabled={enviando}
            >
              <SelectTrigger id="visita-visitante" className={CAMPO_EMBUTIDO}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {opcoes.map((nome) => (
                  <SelectItem key={nome} value={nome}>
                    {nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {form.formState.errors.visitante !== undefined && (
          <p className="mt-1 text-xs text-perigo">{form.formState.errors.visitante.message}</p>
        )}
      </div>

      <div className="flex gap-2 [&>*]:min-w-0 [&>*]:flex-1">
        <div>
          <Label htmlFor="visita-inicio" className={ROTULO_EMBUTIDO}>
            Início
          </Label>
          <Controller
            control={form.control}
            name="inicio"
            render={({ field }) => (
              <CampoMascarado
                id="visita-inicio"
                formato="data"
                value={field.value}
                onChange={field.onChange}
                disabled={enviando}
                className={CAMPO_EMBUTIDO}
              />
            )}
          />
          {form.formState.errors.inicio !== undefined && (
            <p className="mt-1 text-xs text-perigo">{form.formState.errors.inicio.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="visita-fim" className={ROTULO_EMBUTIDO}>
            Fim
          </Label>
          <Controller
            control={form.control}
            name="fim"
            render={({ field }) => (
              <CampoMascarado
                id="visita-fim"
                formato="data"
                value={field.value}
                onChange={field.onChange}
                disabled={enviando}
                className={CAMPO_EMBUTIDO}
              />
            )}
          />
          {form.formState.errors.fim !== undefined && (
            <p className="mt-1 text-xs text-perigo">{form.formState.errors.fim.message}</p>
          )}
        </div>
      </div>

      <div>
        <Label htmlFor="visita-observacao" className={ROTULO_EMBUTIDO}>
          Observação (opcional)
        </Label>
        <Controller
          control={form.control}
          name="observacao"
          render={({ field }) => (
            <Textarea
              id="visita-observacao"
              rows={1}
              placeholder="Ex: ~8h, até 12h..."
              {...field}
              value={field.value ?? ''}
              disabled={enviando}
              className={CAMPO_EMBUTIDO}
            />
          )}
        />
      </div>

      {erroGeral !== null && <p className="text-xs text-perigo">{erroGeral}</p>}

      <div className="flex gap-2">
        <Button type="submit" disabled={enviando} className="flex-1">
          {enviando ? 'Salvando…' : visita === null ? 'Confirmar' : 'Atualizar'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancelar} disabled={enviando}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}
